// Sonido de reservas online (UX §2.5, DS §6.36, DOMAIN AGE-92): dos tonos suaves de 0,6 s, solo si la
// preferencia de este dispositivo está prendida y la pestaña tuvo un gesto previo (los navegadores bloquean
// el audio sin interacción). Se sintetiza con Web Audio: no hace falta un archivo.
import { useSyncExternalStore } from 'react';

export const BOOKING_SOUND_KEY = 'atte.bookingSound';

const listeners = new Set<() => void>();
let gestured = false;
let ctx: AudioContext | null = null;

function readPref(): boolean {
  try {
    return window.localStorage.getItem(BOOKING_SOUND_KEY) !== 'off';
  } catch {
    return true;
  }
}

/** Preferencia del dispositivo (prendida por defecto). */
export function getBookingSound(): boolean {
  return typeof window === 'undefined' ? true : readPref();
}

export function setBookingSound(on: boolean): void {
  try {
    window.localStorage.setItem(BOOKING_SOUND_KEY, on ? 'on' : 'off');
  } catch {
    /* almacenamiento bloqueado */
  }
  listeners.forEach((l) => l());
}

export function useBookingSound(): [boolean, (on: boolean) => void] {
  const on = useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    getBookingSound,
    () => true,
  );
  return [on, setBookingSound];
}

/** Registra el primer gesto de la pestaña (lo llama la app una vez). */
export function trackUserGesture(): () => void {
  const mark = () => {
    gestured = true;
    // Crear (o reanudar) el contexto dentro del gesto evita el bloqueo de autoplay.
    try {
      const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (AC && !ctx) ctx = new AC();
      void ctx?.resume();
    } catch {
      /* sin audio */
    }
  };
  window.addEventListener('pointerdown', mark, { once: true, capture: true });
  window.addEventListener('keydown', mark, { once: true, capture: true });
  return () => {
    window.removeEventListener('pointerdown', mark, { capture: true });
    window.removeEventListener('keydown', mark, { capture: true });
  };
}

export function hadUserGesture(): boolean {
  return gestured;
}

/** Suena el aviso si corresponde (preferencia + gesto previo). Nunca lanza. */
export function playBookingChime(): void {
  if (!gestured || !getBookingSound() || !ctx) return;
  try {
    const now = ctx.currentTime;
    const tones: Array<[number, number]> = [
      [880, 0],
      [1318.5, 0.18],
    ];
    for (const [freq, at] of tones) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now + at);
      gain.gain.exponentialRampToValueAtTime(0.18, now + at + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + at + 0.42);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + at);
      osc.stop(now + at + 0.45);
    }
  } catch {
    /* sin audio */
  }
}
